// Constants
import { TooltipHost, useTheme } from "@fluentui/react";
import { TaskAssignWarningDialogClassName } from "@ms/uno-constants/lib/local/DialogConstants";
import { AlertUserDebounceInterval } from "@ms/uno-constants/lib/local/NotificationConstants";
import { MaxSubstrateSearchQueryTermLength } from "@ms/uno-constants/lib/local/SubstrateConstants";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// Components
import { DirectionalHint, FocusTrapZone, FocusZone, FocusZoneDirection, Icon, Persona, PersonaSize, Spinner } from "@fluentui/react";
import { TextField, TextFieldType } from "../../textField/TextField";
import { Callout } from "../callout/Callout";
import { UserContainer } from "../user/UserContainer";
import { VirtualListOrientation, VirtualListWrapper } from "../virtualization/VirtualListWrapper";
import { AssigneeSuggestionContext } from "@ms/uno-models/lib/local/client/intelligence/IAssigneeSuggestions";
// Utilities
import * as StringHelper from "@fluentui/utilities";
import { compareByDisplayName } from "@ms/uno-utilities/lib/local/Comparators";
import { ChangeFocusBeforeRemovalIFocusable } from "@ms/uno-utilities/lib/local/FocusUtilities";
import debounce from "lodash/debounce";
import * as React from "react";
// Styles
import { assignIconProps, dismissIconProps, infoIconProps } from "../../../icons/icons.styles";
import { getCalloutStyles } from "../../contextualMenu/ContextualMenu.styles";
import { getClassNames, getInfoTooltipStyles, getStyles } from "../assignmentPicker/AssignmentPicker.styles";
// Errors
import { ArgumentError, ArgumentOutOfRangeError } from "@ms/uno-errors/lib/local/errors";
/** Default page size of the scrolling list of suggestions */ const DefaultScrollPageSize = 5;
/**
 * Props for the Assignment Control
 */ export function AssignmentPicker(props) {
    const { onGivePlanAccess, canEditAssignments, onRenderPersona, assignees, assignedAgent, zeroTermQueryResults, resourcesWithAccess, availableAgents, canEditAccessPermissions, canAssignToResourcesWithoutAccess, canGiveAccessToGuests, minQueryTermLength, canAccessPlanViaSharedContainerOnly, maxAssignees, showAssigneeLimitError, currentUserId, onUserSearchQueryChanged, getUsersByKeyword, isUsersByKeywordDataReady, alertUser, targetElement, onAssignCalloutDismiss, preventDismissOnScroll, onAssignUser, onAssignAgent, onUnassignAgent, scrollPageSize, getUserPhoto, strings, agentsInfoContentMap } = props;
    const { assignmentFilterPlaceholderText, assignmentFilterAriaLabel, unassignUserButtonText, giveAccessTooltipText, personaRoleGuest, personaRoleNonMember, personaCompoundRole, assignedSectionHeader, queryLoadingText, suggestedSectionHeader } = strings;
    const theme = useTheme();
    /** Number of suggestions on each scrolling increment. Used for virtualization */ const scrollSize = scrollPageSize ?? DefaultScrollPageSize;
    validateProps(props);
    const [searchTerm, setSearchTerm] = useState("");
    const [totalSuggestionsToRender, setTotalSuggestionsToRender] = useState(scrollSize);
    const [shouldAlertUser, setShouldAlertUser] = useState(false);
    const maxHeight = useMemo(()=>computeMaxHeight(scrollSize), [
        scrollSize
    ]);
    const classNames = getClassNames(getStyles(theme, props.styles, !canEditAssignments));
    const listStyles = {
        maxHeight: maxHeight
    };
    const assignedUserButtonsRef = useRef([]);
    const suggestedUserButtonsRef = useRef([]);
    const searchInputElementRef = useRef(null);
    const accessMap = useMemo(()=>({}), []);
    props.resourcesWithAccess.forEach((user)=>{
        accessMap[user.id] = true;
    });
    const isWaitingOnQuery = useRef(false);
    /**
     * Check if the maximum assignee limit has been reached. If so show the error dialog
     */ const checkIfReachedAssigneeLimit = useCallback(()=>{
        const currentAssignees = assignees;
        if (maxAssignees && currentAssignees.length >= maxAssignees) {
            showAssigneeLimitError();
            return true;
        }
        return false;
    }, [
        maxAssignees,
        showAssigneeLimitError,
        assignees
    ]);
    useEffect(()=>{
        // Set focus on search term text input when the contents have been cleared
        if (searchTerm === "") {
            searchInputElementRef.current?.focus();
        }
    }, [
        searchTerm
    ]);
    // Clear searchTerm
    const onAssignSuccess = useCallback(()=>{
        setSearchTerm("");
    }, []);
    /**
     * Callback for when a user is assigned to
     * Note: If there are already the maximum number of assignees this will open an error dialog
     */ const onAssignUserCallback = useCallback((userId)=>{
        if (checkIfReachedAssigneeLimit()) {
            return;
        }
        let altElement = searchInputElementRef.current;
        if (assignedUserButtonsRef.current.length > 0) {
            altElement = assignedUserButtonsRef.current[assignedUserButtonsRef.current.length - 1].element;
        }
        ChangeFocusBeforeRemovalIFocusable(suggestedUserButtonsRef.current, ()=>altElement, userId);
        onAssignUser(userId, altElement, onAssignSuccess);
    }, [
        assignedUserButtonsRef,
        checkIfReachedAssigneeLimit,
        onAssignUser,
        suggestedUserButtonsRef,
        onAssignSuccess
    ]);
    /**
     * Callback for when the last element in the suggestions list is rendered
     */ const onSuggestionsPageLoaded = useCallback(()=>{
        setTotalSuggestionsToRender(totalSuggestionsToRender + scrollSize);
    }, [
        scrollSize,
        totalSuggestionsToRender
    ]);
    /**
     * Render the suggested assignees section based on the zt-q result, users with access and the current query results
     */ const renderSuggestedSection = (suggestions, suggestedAgents)=>{
        const suggestionsToRender = suggestions.slice(0, totalSuggestionsToRender);
        let hasMoreItemsToRender = false;
        if (totalSuggestionsToRender < suggestions.length) {
            // When Rendering last element we add observable that handles adding more elements to render
            hasMoreItemsToRender = true;
        }
        const loadingSpinner = isWaitingOnQuery.current ? /*#__PURE__*/ React.createElement(Spinner, {
            label: queryLoadingText
        }) : null;
        if (suggestionsToRender.length < 1 && suggestedAgents.length < 1 || canEditAssignments === false) {
            return null;
        }
        const agentSuggestions = suggestedAgents.map((agentPersonaProps)=>{
            const accessInfoContent = agentsInfoContentMap?.[agentPersonaProps.agent.id];
            return /*#__PURE__*/ React.createElement("div", {
                className: classNames.user,
                key: agentPersonaProps.agent.id
            }, /*#__PURE__*/ React.createElement("button", {
                className: classNames.agentOptionButton,
                onClick: (evt)=>{
                    evt.stopPropagation();
                    onAssignAgent(agentPersonaProps.agent.id);
                },
                role: "option",
                "aria-selected": false
            }, /*#__PURE__*/ React.createElement("div", {
                className: classNames.agentButtonContent
            }, /*#__PURE__*/ React.createElement(Persona, agentPersonaProps), accessInfoContent && /*#__PURE__*/ React.createElement(TooltipHost, {
                content: accessInfoContent,
                calloutProps: getInfoTooltipStyles(),
                id: "accessInfoTooltip",
                directionalHint: DirectionalHint.bottomAutoEdge
            }, /*#__PURE__*/ React.createElement(Icon, {
                ...infoIconProps,
                "data-is-focusable": true,
                className: "infoIcon",
                "aria-label": accessInfoContent
            })))));
        });
        const suggestionElements = suggestionsToRender.map((suggestion)=>{
            return /*#__PURE__*/ React.createElement("div", {
                className: classNames.user,
                key: suggestion.id
            }, /*#__PURE__*/ React.createElement("button", {
                "aria-label": suggestion.secondaryText ? `${suggestion.user.displayName}, ${suggestion.secondaryText}` : suggestion.user.displayName,
                onClick: (evt)=>{
                    evt.stopPropagation();
                    if (!canEditAssignments) {
                        return;
                    }
                    onAssignUserCallback(suggestion.user.id);
                },
                ref: (button)=>{
                    if (button != null) {
                        suggestedUserButtonsRef.current.push({
                            element: button,
                            id: suggestion.user.id
                        });
                    }
                },
                role: "option",
                "aria-selected": false
            }, /*#__PURE__*/ React.createElement(UserContainer, {
                user: suggestion.user,
                onLoad: onRenderPersona,
                containerId: `userContainer-peoplePicker-suggested-${suggestion.user.id}`
            }, /*#__PURE__*/ React.createElement(Persona, suggestion))));
        });
        return /*#__PURE__*/ React.createElement("div", {
            className: "userListSection",
            role: "listbox"
        }, /*#__PURE__*/ React.createElement("div", {
            className: "sectionTitle"
        }, suggestedSectionHeader), /*#__PURE__*/ React.createElement(FocusZone, {
            "aria-label": suggestedSectionHeader,
            className: "userList",
            direction: FocusZoneDirection.vertical,
            isCircularNavigation: false
        }, /*#__PURE__*/ React.createElement(VirtualListWrapper, {
            id: "userlist-suggestions",
            hasMoreItemsToRender: hasMoreItemsToRender,
            onLastElementIsVisible: onSuggestionsPageLoaded,
            orientation: VirtualListOrientation.Vertical
        }, agentSuggestions, suggestionElements), loadingSpinner));
    };
    /**
     * Render the empty list UX
     */ const renderEmptyResultsList = ()=>{
        const style = {
            maxHeight: maxHeight
        };
        return /*#__PURE__*/ React.createElement("div", {
            className: "userListSection emptySection",
            style: style
        }, /*#__PURE__*/ React.createElement("span", null, strings.noResultsFoundText));
    };
    /**
     * Callback for when a user is unassigned from
     * @param userId User to unassign
     */ const onUnassignUser = (userId)=>{
        ChangeFocusBeforeRemovalIFocusable(suggestedUserButtonsRef.current, ()=>searchInputElementRef, userId);
        props.onUnassignUser(userId);
    };
    /**
     * Render an action button for an assignment
     */ const renderAssignmentActionButton = (id, iconProps, tooltip, onClick)=>{
        return /*#__PURE__*/ React.createElement("button", {
            className: "personaActionButton",
            onClick: ()=>onClick(id),
            title: tooltip,
            "aria-label": tooltip,
            role: "option",
            ref: (button)=>{
                if (button != null) {
                    assignedUserButtonsRef.current.push({
                        element: button,
                        id: id
                    });
                }
            }
        }, /*#__PURE__*/ React.createElement(Icon, iconProps));
    };
    /**
     * Renders unassign button for agent
     */ const renderUnassignAgentButton = (agent)=>{
        return renderAssignmentActionButton(agent.id, dismissIconProps, "", onUnassignAgent);
    };
    /**
     * Renders the give access button
     */ const renderGiveAccessButton = (personaProps)=>{
        // TODO: Consider allocating this once via memoization instead of per-render.
        const tooltip = StringHelper.format(giveAccessTooltipText, personaProps.text);
        return renderAssignmentActionButton(personaProps.user.id, assignIconProps, tooltip, onGivePlanAccess);
    };
    /**
     * Renders the unassign button
     */ const renderUnassignButton = (personaProps)=>{
        // TODO: Consider allocating this once via memoization instead of per-render.
        const tooltip = StringHelper.format(unassignUserButtonText, personaProps.text);
        return renderAssignmentActionButton(personaProps.user.id, dismissIconProps, tooltip, onUnassignUser);
    };
    /**
     * Render the current assignees section
     */ const renderAssignmentSection = (assignees, assignedAgentPersonaProps)=>{
        if (assignees.length < 1 && !assignedAgentPersonaProps) {
            return null;
        }
        const assignedAgentPersona = assignedAgentPersonaProps ? /*#__PURE__*/ React.createElement("div", {
            className: classNames.user,
            key: assignedAgentPersonaProps?.agent.id
        }, /*#__PURE__*/ React.createElement(Persona, assignedAgentPersonaProps), renderUnassignAgentButton(assignedAgentPersonaProps.agent)) : null;
        const canUserGivePlanAccess = canEditAccessPermissions && !canAccessPlanViaSharedContainerOnly;
        return /*#__PURE__*/ React.createElement("div", {
            className: "userListSection",
            role: "listbox"
        }, /*#__PURE__*/ React.createElement("div", {
            className: "sectionTitle"
        }, assignedSectionHeader), /*#__PURE__*/ React.createElement(FocusZone, {
            "aria-label": assignedSectionHeader,
            className: "userList",
            direction: FocusZoneDirection.domOrder,
            isCircularNavigation: false
        }, assignedAgentPersona, assignees.map((personaProps)=>{
            return /*#__PURE__*/ React.createElement("div", {
                className: classNames.user,
                key: personaProps.user.id
            }, /*#__PURE__*/ React.createElement(UserContainer, {
                user: personaProps.user,
                onLoad: onRenderPersona,
                containerId: `userContainer-peoplePicker-assigned-${personaProps.user.id}`
            }, /*#__PURE__*/ React.createElement(Persona, personaProps)), !personaProps.hasAccess && canUserGivePlanAccess && renderGiveAccessButton(personaProps), (canEditAssignments || currentUserId === personaProps.user.id) && renderUnassignButton(personaProps));
        })));
    };
    /** Get agent persona props for an agent */ function getAgentPersonaProps(agent) {
        return {
            id: agent.id,
            text: agent.displayName,
            imageUrl: agent.logoUrl,
            size: PersonaSize.size32,
            agent: agent
        };
    }
    /**
     * Get persona props for users in picker list
     */ const getAssigneePersonaProps = useCallback((user, context)=>{
        const hasAccess = accessMap[user.id] === true;
        const guestRoleString = personaRoleGuest;
        const nonAccessRoleString = personaRoleNonMember;
        let subText = "";
        // Show context string for recommended assignee
        if (context !== undefined) {
            subText = StringHelper.format(mapContextEnumtoString(context, strings), user.displayName);
        }
        // If assigner is not a plan member (i.e. has access via shared container only),
        // they shouldn't know about the assignee's member info
        if (user.isGuest() && !hasAccess) {
            subText = StringHelper.format(personaCompoundRole, guestRoleString, nonAccessRoleString);
            subText = canAccessPlanViaSharedContainerOnly ? "" : subText;
        } else if (user.isGuest()) {
            subText = guestRoleString;
        } else if (!hasAccess) {
            subText = canAccessPlanViaSharedContainerOnly ? "" : nonAccessRoleString;
        }
        return {
            id: user.id,
            text: user.displayName,
            secondaryText: subText,
            imageUrl: getUserPhoto(user.id) ?? undefined,
            size: PersonaSize.size32,
            user: user,
            hasAccess: hasAccess,
            showSecondaryText: subText.length > 0
        };
    }, [
        accessMap,
        personaCompoundRole,
        personaRoleGuest,
        personaRoleNonMember,
        canAccessPlanViaSharedContainerOnly,
        strings,
        getUserPhoto
    ]);
    /**
     * Check whether the agent matches the search criteria
     */ const agentMatchesSearchCriteria = useCallback((agent)=>{
        if (searchTerm === "") {
            return true;
        }
        return agent.displayName.toLocaleLowerCase().indexOf(searchTerm.toLocaleLowerCase()) !== -1;
    }, [
        searchTerm
    ]);
    /**
     * Check whether a user matches the current search criteria
     */ const userMatchesSearchCriteria = useCallback((user)=>{
        if (searchTerm === "") {
            return true;
        }
        let matchesProxyAddresses = false;
        user.proxyAddresses.forEach((address)=>{
            if (address.toLocaleLowerCase().indexOf(searchTerm.toLocaleLowerCase()) !== -1) {
                matchesProxyAddresses = true;
            }
        });
        return matchesProxyAddresses || user.displayName.toLocaleLowerCase().indexOf(searchTerm.toLocaleLowerCase()) !== -1 || user.mail && user.mail.toLocaleLowerCase().indexOf(searchTerm.toLocaleLowerCase()) !== -1 || user.userPrincipalName.toLocaleLowerCase().indexOf(searchTerm.toLowerCase()) !== -1;
    }, [
        searchTerm
    ]);
    /**
     * Get the persona props for the suggested agents based on user search query and available agents
     */ const suggestedAgentPersonas = useMemo(()=>{
        let suggestedAgentsList = [];
        // Sort the agents in alphabetical order by display name
        suggestedAgentsList = availableAgents.sort((first, second)=>compareByDisplayName(first, second, (agent)=>agent.displayName));
        // Remove the agent that is currently assigned
        const assignedAgentId = assignedAgent?.id ?? "";
        suggestedAgentsList = suggestedAgentsList.filter((agent)=>agent.id !== assignedAgentId);
        // Filter the agents based on the search criteria
        suggestedAgentsList = suggestedAgentsList.filter((agent)=>agentMatchesSearchCriteria(agent));
        // Map the agents into persona props
        return suggestedAgentsList.map((agent)=>getAgentPersonaProps(agent));
    }, [
        agentMatchesSearchCriteria,
        availableAgents,
        assignedAgent
    ]);
    /**
     * Generate the set of personas for the suggested section using the zt-q results + users with access to the plan + search query results
     */ const suggestedPersonas = useMemo(()=>{
        let suggestionsList = [];
        //Sort resources with access into alphabetical order by display name
        const sortedResourcesWithAccess = resourcesWithAccess.sort((first, second)=>compareByDisplayName(first, second, (user)=>user.displayName));
        // Show suggestions if zero term query results are non-null and the search term length is under the threshold.
        if (zeroTermQueryResults && searchTerm.length < minQueryTermLength) {
            suggestionsList = zeroTermQueryResults.sort((a, b)=>{
                return (b.weight ?? 0) - (a.weight ?? 0);
            });
            const assigneeSuggestionsListIds = zeroTermQueryResults.map((user)=>user.id);
            const remainingUsers = sortedResourcesWithAccess.filter((user)=>assigneeSuggestionsListIds.indexOf(user.id) < 0);
            suggestionsList = suggestionsList.concat(remainingUsers);
        } else {
            suggestionsList = sortedResourcesWithAccess;
        }
        const isMinQueryLength = searchTerm.length >= minQueryTermLength;
        isWaitingOnQuery.current = isMinQueryLength && isUsersByKeywordDataReady ? !isUsersByKeywordDataReady(searchTerm) : false;
        const isGetUserByKeywordDataReady = isUsersByKeywordDataReady ? isUsersByKeywordDataReady(searchTerm) : false;
        // Filter only the plan members by query term
        // We don't want to filter 3S results by exact query term because 3S can include results similar to the query term
        suggestionsList = suggestionsList.filter((user)=>{
            // Filter for query terms if any
            return userMatchesSearchCriteria(user);
        });
        if (isMinQueryLength && getUsersByKeyword && isGetUserByKeywordDataReady) {
            // We are in querying mode. Use query results directly after filtering for search criteria
            const queryResults = getUsersByKeyword(searchTerm);
            const suggestionsListIds = suggestionsList.map((user)=>user.id);
            const filteredQueryResults = queryResults.filter((user)=>suggestionsListIds.indexOf(user.id) === -1);
            suggestionsList = suggestionsList.concat(filteredQueryResults);
        }
        // Final filtering of the list
        const assigneeIds = assignees.map((user)=>user.id);
        suggestionsList = suggestionsList.filter((user)=>{
            // If the user cannot assign to resources with access, we need to filter specific classes of suggestions
            if (!canAssignToResourcesWithoutAccess) {
                if (!canEditAccessPermissions) {
                    // Need to filter out non members from the suggestions since user cannot assign the task to them anyways
                    if (accessMap[user.id] === false) {
                        return false;
                    }
                } else if (!canGiveAccessToGuests) {
                    // Need to filter out non members "guests" from the suggestions since user cannot give access to them
                    if (user.isGuest() && accessMap[user.id] === false) {
                        return false;
                    }
                }
            }
            // Filter out assignees
            return assigneeIds.indexOf(user.id) < 0;
        });
        // Finally map the list into AssignmentPersonaProps
        return suggestionsList.map((user)=>{
            return getAssigneePersonaProps(user, user.context);
        });
    }, [
        assignees,
        canAssignToResourcesWithoutAccess,
        canEditAccessPermissions,
        canGiveAccessToGuests,
        getAssigneePersonaProps,
        isUsersByKeywordDataReady,
        resourcesWithAccess,
        searchTerm,
        userMatchesSearchCriteria,
        zeroTermQueryResults,
        minQueryTermLength,
        accessMap,
        getUsersByKeyword
    ]);
    /** Method to get assigned agent's persona */ const assignedAgentPersona = assignedAgent && agentMatchesSearchCriteria(assignedAgent) ? getAgentPersonaProps(assignedAgent) : undefined;
    /** Method to get assigned personas */ const assignedPersonas = useMemo(()=>{
        // Do not sort assigned personas since they are ordered by when they were assigned
        return assignees.filter((assignee)=>userMatchesSearchCriteria(assignee)).map((assignee)=>getAssigneePersonaProps(assignee));
    }, [
        assignees,
        userMatchesSearchCriteria,
        getAssigneePersonaProps
    ]);
    const assignedSection = renderAssignmentSection(assignedPersonas, assignedAgentPersona);
    const suggestedSection = renderSuggestedSection(suggestedPersonas, suggestedAgentPersonas);
    /** Alert user with number of search/filter results */ useEffect(()=>{
        const isGetUserByKeywordDataReady = isUsersByKeywordDataReady ? isUsersByKeywordDataReady(searchTerm) : false;
        // If the user initiated a search and the data is ready, alert the user:
        //     - If getUsersByKeyword is undefined (user cannot search the tenant), there is no additional result data to wait for, so alert the user
        //     - Otherwise, if the result data is ready, alert the user
        if (shouldAlertUser && (!getUsersByKeyword || isGetUserByKeywordDataReady)) {
            setShouldAlertUser(false);
            alertUser(suggestedPersonas.length + assignedPersonas.length);
        }
    }, [
        assignedPersonas,
        suggestedPersonas,
        searchTerm,
        shouldAlertUser,
        alertUser,
        getUsersByKeyword,
        isUsersByKeywordDataReady
    ]);
    /**
     * Invoke the search callback for users that match the search query term
     */ const debouncedQueryDelegate = useMemo(()=>debounce((value)=>{
            if (value.length < minQueryTermLength) {
                return;
            }
            setShouldAlertUser(true);
            if (!onUserSearchQueryChanged) {
                return;
            }
            onUserSearchQueryChanged(value);
        }, AlertUserDebounceInterval), [
        onUserSearchQueryChanged,
        minQueryTermLength
    ]);
    /*** Clean up any side effects from debounce when component gets unmounted */ useEffect(()=>{
        return ()=>{
            debouncedQueryDelegate.cancel();
        };
    }, [
        debouncedQueryDelegate
    ]);
    /**
     * Handler for when the search query text changes
     */ const searchTextChanged = useCallback((event, newValue)=>{
        setSearchTerm(newValue);
        debouncedQueryDelegate(newValue);
    }, [
        debouncedQueryDelegate
    ]);
    //render component
    return /*#__PURE__*/ React.createElement(Callout, {
        isBeakVisible: false,
        gapSpace: 0,
        doNotLayer: false,
        target: targetElement,
        directionalHint: DirectionalHint.bottomLeftEdge,
        onDismiss: onAssignCalloutDismiss,
        preventDismissOnEvent: preventDismissOnEvent,
        preventDismissOnScroll: preventDismissOnScroll,
        setInitialFocus: false,
        className: classNames.root,
        styles: getCalloutStyles(theme),
        role: "complementary"
    }, /*#__PURE__*/ React.createElement(FocusTrapZone, {
        isClickableOutsideFocusTrap: true
    }, props.canEditAssignments && /*#__PURE__*/ React.createElement(TextField, {
        autoFocus: true,
        tabIndex: 0,
        placeholder: assignmentFilterPlaceholderText,
        ariaLabel: assignmentFilterAriaLabel,
        onChange: searchTextChanged,
        value: searchTerm,
        componentRef: (component)=>{
            searchInputElementRef.current = component;
        },
        autoComplete: "new-password",
        styleType: TextFieldType.Dialog,
        className: classNames.searchField,
        maxLength: MaxSubstrateSearchQueryTermLength
    }), /*#__PURE__*/ React.createElement("div", {
        className: classNames.userLists,
        style: listStyles
    }, assignedSection, suggestedSection, searchTerm.length > 0 && assignedPersonas.length === 0 && suggestedPersonas.length === 0 && renderEmptyResultsList())));
}
/**
 * Validate the props passed to the component
 */ function validateProps(props) {
    if (props.maxAssignees != null && props.maxAssignees < 0) {
        throw new ArgumentError("maxAssignees");
    }
    if (props.scrollPageSize != null && props.scrollPageSize < 3) {
        throw new ArgumentOutOfRangeError("scrollPageSize");
    }
}
/**
 * Callback to determine whether to prevent dismiss on event
 */ function preventDismissOnEvent() {
    // Don't dismiss if the warning dialog is displayed
    if (document.getElementsByClassName(TaskAssignWarningDialogClassName)[0]) {
        return true;
    }
    return false;
}
/**
 * Helper function to change the context enum to a string
 * @param context context enum from the user
 * @param strings strings for the assignment picker
 */ const mapContextEnumtoString = (context, strings)=>{
    const { suggestedAssigneeSimilarTitle, suggestedAssigneeUserTopAssignee, suggestedAssigneePlanTopAssignee, suggestedAssigneeMostAvailableUser } = strings;
    switch(context){
        case AssigneeSuggestionContext.SimilarTaskTitle:
            return suggestedAssigneeSimilarTitle;
        case AssigneeSuggestionContext.UserTopAssignee:
            return suggestedAssigneeUserTopAssignee;
        case AssigneeSuggestionContext.PlanTopAssignee:
            return suggestedAssigneePlanTopAssignee;
        case AssigneeSuggestionContext.MostAvailableUser:
            return suggestedAssigneeMostAvailableUser;
        default:
            return "";
    }
};
/**
 * Compute the current max height
 */ function computeMaxHeight(scrollSize) {
    let numberOfRenderedSuggestions = scrollSize ?? 0;
    if (numberOfRenderedSuggestions < 3) {
        // Clip to minimum of 3 suggestions
        numberOfRenderedSuggestions = 3;
    }
    // Compute max height based on 60px for 2 sections + number of items to show * 50px per suggestion
    return 60 + numberOfRenderedSuggestions * 50;
}
